import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import axios from "axios";

import {
    Link,
    useSearchParams,
} from "react-router-dom";

import Layout from "../components/layoutBlack";
import Breadcrumbs from "../components/breadcrumbs";
import { config } from "../utils/constants";


const REACT_APP_API_URL =
    config.url.REACT_APP_API_URL;


const getErrorMessage = (error) => {
    const responseData =
        error.response?.data;

    if (!responseData) {
        return (
            "We could not connect to the server. " +
            "Please check your connection and try again."
        );
    }

    if (typeof responseData === "string") {
        return responseData;
    }

    if (
        typeof responseData.detail ===
        "string"
    ) {
        return responseData.detail;
    }

    if (
        typeof responseData.message ===
        "string"
    ) {
        return responseData.message;
    }

    if (
        Array.isArray(
            responseData.non_field_errors
        )
    ) {
        return responseData
            .non_field_errors
            .join(" ");
    }

    if (
        Array.isArray(responseData.token)
    ) {
        return responseData.token.join(" ");
    }

    if (
        typeof responseData.token ===
        "string"
    ) {
        return responseData.token;
    }

    return (
        "The verification link is invalid, expired, " +
        "or has already been used."
    );
};


const WholesaleRegistrationVerification =
    () => {
        const [
            searchParams,
        ] = useSearchParams();

        const verificationStarted =
            useRef(false);

        const [
            status,
            setStatus,
        ] = useState("verifying");

        const [
            message,
            setMessage,
        ] = useState(
            "Please wait while we verify your email address."
        );

        const [
            wholesaleStatus,
            setWholesaleStatus,
        ] = useState(null);

        const token =
            searchParams.get("token")?.trim() ||
            "";

        useEffect(() => {
            window.scrollTo(0, 0);
        }, []);

        useEffect(() => {
            /*
             * React StrictMode may run effects twice during
             * development. This prevents two verification
             * requests from being sent.
             */
            if (verificationStarted.current) {
                return;
            }

            verificationStarted.current = true;

            const verifyRegistration =
                async () => {
                    if (!token) {
                        setStatus("error");

                        setMessage(
                            "The verification token is missing from this link."
                        );

                        return;
                    }

                    try {
                        const response =
                            await axios.post(
                                `${REACT_APP_API_URL}/wholesale/verify-registration/`,
                                {
                                    token,
                                },
                                {
                                    withCredentials:
                                        true,
                                }
                            );

                        setWholesaleStatus(
                            response.data
                                ?.wholesale_status ||
                                "pending"
                        );

                        setStatus("success");

                        setMessage(
                            response.data?.message ||
                                (
                                    "Your email has been verified. " +
                                    "Your wholesale application is " +
                                    "now awaiting approval."
                                )
                        );
                    } catch (error) {
                        setStatus("error");

                        setMessage(
                            getErrorMessage(error)
                        );
                    }
                };

            verifyRegistration();
        }, [token]);

        return (
            <div>
                <Layout>
                    <div className="container p-t-80 p-b-10">
                        <Breadcrumbs />
                    </div>

                    <section className="full-page-section bg0 p-t-40 p-b-125">
                        <div className="container">
                            <div
                                className="m-lr-auto"
                                style={{
                                    maxWidth:
                                        "720px",
                                }}
                            >
                                <div className="bor10 p-lr-70 p-t-55 p-b-70 p-lr-15-lg">
                                    <div
                                        className="txt-center"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        {status ===
                                        "verifying" ? (
                                            <>
                                                <h1 className="mtext-105 cl2 p-b-20">
                                                    Verifying
                                                    Your Email
                                                </h1>

                                                <p className="stext-115 cl6">
                                                    {message}
                                                </p>

                                                <div className="p-t-30">
                                                    <span
                                                        className="spinner-border"
                                                        role="presentation"
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                            </>
                                        ) : null}

                                        {status ===
                                        "success" ? (
                                            <>
                                                <h1 className="mtext-105 cl2 p-b-20">
                                                    Email
                                                    Verified
                                                </h1>

                                                <div
                                                    className="alert alert-success m-b-25"
                                                    role="alert"
                                                >
                                                    {
                                                        message
                                                    }
                                                </div>

                                                {wholesaleStatus ===
                                                "pending" ? (
                                                    <p className="stext-102 cl6 p-b-25">
                                                        Your
                                                        wholesale
                                                        application
                                                        has been
                                                        successfully
                                                        submitted
                                                        for review.
                                                        You will
                                                        receive a
                                                        decision by
                                                        email within
                                                        48 hours.
                                                    </p>
                                                ) : null}

                                                <p className="stext-107 cl6 p-b-30">
                                                    You will not
                                                    be able to
                                                    sign in until
                                                    your wholesale
                                                    account has
                                                    been approved.
                                                </p>

                                                <Link
                                                    to="/"
                                                    className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04"
                                                >
                                                    Return Home
                                                </Link>
                                            </>
                                        ) : null}

                                        {status ===
                                        "error" ? (
                                            <>
                                                <h1 className="mtext-105 cl2 p-b-20">
                                                    Verification
                                                    Unsuccessful
                                                </h1>

                                                <div
                                                    className="alert alert-danger m-b-25"
                                                    role="alert"
                                                >
                                                    {
                                                        message
                                                    }
                                                </div>

                                                <p className="stext-102 cl6 p-b-30">
                                                    The link may
                                                    have expired,
                                                    already been
                                                    used, or may
                                                    be incomplete.
                                                </p>

                                                <div className="d-flex flex-column gap-3">
                                                    <Link
                                                        to="/wolesale-registration"
                                                        className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04"
                                                    >
                                                        Return to
                                                        Wholesale
                                                        Registration
                                                    </Link>

                                                    <Link
                                                        to="/"
                                                        className="stext-102 cl1 hov-cl1 trans-04"
                                                    >
                                                        Return Home
                                                    </Link>
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </Layout>
            </div>
        );
    };


export default WholesaleRegistrationVerification;